import { Graphics } from "pixi.js";
import type { SceneGraph, NodeRT } from "../types.ts";
import type { Camera } from "../Camera.ts";
import type { CosmosData } from "../../parser/types.ts";

const hex = (s: string) => parseInt(s.slice(1), 16);

/**
 * Conexões: secundárias (wikilinks, tênues) e primárias (pai→filho, curvas).
 * Desenhadas em espaço-mundo (no container world); largura compensada pelo zoom.
 * Partículas de fluxo correm pelas conexões importantes.
 */
export class Links {
  readonly gfx = new Graphics();

  constructor(private data: CosmosData) {}

  setData(data: CosmosData) { this.data = data; }

  update(
    scene: SceneGraph, cam: Camera, time: number,
    related: Set<string> | null,
    galColor: (g: string) => number,
    focusGal: string | null, dimAmt: number,
    isActive: (n: NodeRT) => boolean,
  ) {
    const g = this.gfx;
    g.clear();
    const { byId } = scene;
    const wz = 1 / cam.z;
    const galDim = (n: NodeRT) =>
      focusGal && n.gal !== focusGal && n.kind !== "council" ? 1 - 0.9 * dimAmt : 1;

    // secundárias
    for (const e of this.data.links) {
      const a = byId.get(e.s), b = byId.get(e.t);
      if (!a || !b || !isActive(a) || !isActive(b)) continue;
      let al = 0.05;
      if (related) al *= related.has(a.id) || related.has(b.id) ? 2.4 : 0.12;
      al *= Math.min(galDim(a), galDim(b));
      if (al < 0.012) continue;
      const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2, o = 0.14;
      g.moveTo(a.x, a.y).quadraticCurveTo(
        mx + (a.y - b.y) * o, my + (b.x - a.x) * o, b.x, b.y,
      ).stroke({ width: 0.6 * wz, color: 0x8aa0d8, alpha: al });
    }

    // primárias (pai→filho)
    for (const n of scene.rt) {
      if (!n.parent) continue;
      const p = byId.get(n.parent);
      if (!p || !isActive(n) || !isActive(p)) continue;
      let ax = p.x, ay = p.y;
      // nasce do halo do Sol
      if (p.kind === "council") {
        const dx = n.x - ax, dy = n.y - ay, d = Math.hypot(dx, dy) || 1, hr = p.r * 1.42;
        ax += (dx / d) * hr; ay += (dy / d) * hr;
      }
      const base = n.depth <= 2 ? 0.42 : n.depth === 3 ? 0.22 : 0.1;
      let al = base * galDim(n);
      if (related) al *= related.has(n.id) || related.has(p.id) ? 1.7 : 0.1;
      if (al < 0.012) continue;
      const col = galColor(n.gal);
      const mx = (ax + n.x) / 2, my = (ay + n.y) / 2, o = 0.16 * Math.sin(n.ang || 0.5);
      const cx = mx + (ay - n.y) * o, cy = my + (n.x - ax) * o;
      const w = Math.max(0.5, (3.4 - n.depth) * 0.6) * wz * (1 + 0.6 * n.hl);
      g.moveTo(ax, ay).quadraticCurveTo(cx, cy, n.x, n.y).stroke({ width: w, color: col, alpha: al });

      // partícula de fluxo
      if (n.depth <= 2) {
        const tp = (time * 0.18 + n.seed) % 1;
        const u = 1 - tp;
        const fx = u * u * ax + 2 * u * tp * cx + tp * tp * n.x;
        const fy = u * u * ay + 2 * u * tp * cy + tp * tp * n.y;
        g.circle(fx, fy, 1.8 * wz).fill({ color: col, alpha: 0.6 * galDim(n) });
      }
    }
  }
}

export function colorMap(data: CosmosData): (g: string) => number {
  const m = new Map<string, number>();
  for (const [k, v] of Object.entries(data.gal)) m.set(k, hex(v.color));
  return (g: string) => m.get(g) ?? 0xffffff;
}
